/**
 * Состояние раздела базы знаний: выбранное пространство, дерево страниц и
 * открытая страница.
 *
 * Живёт стором, а не в экране, потому что дерево показывает сайдбар, а
 * страницу — основная область: два места читают одно и то же, и грузить дерево
 * дважды незачем.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import {
  createPage,
  deletePage,
  getPage,
  getTree,
  listIssueLinks,
  listSpaces,
  listVersions,
  movePage,
  restoreVersion,
  saveVersion,
  toTree,
  updatePage,
  type Page,
  type PageBrief,
  type PageIssueLink,
  type PageVersion,
  type Space,
} from './api'

const SPACE_KEY = 'tracker-kb-space'

export const useKnowledgeStore = defineStore('knowledge', () => {
  const spaces = ref<Space[]>([])
  const spaceId = ref('')
  const pages = ref<PageBrief[]>([])
  const page = ref<Page | null>(null)
  const links = ref<PageIssueLink[]>([])
  const versions = ref<PageVersion[]>([])
  const isLoading = ref(false)
  const error = ref('')

  const tree = computed(() => toTree(pages.value))
  const space = computed(() => spaces.value.find((entry) => entry.id === spaceId.value) ?? null)

  /** Выбранное пространство помним между заходами: оно у человека обычно одно. */
  const remember = (id: string) => {
    if (typeof window !== 'undefined') window.localStorage.setItem(SPACE_KEY, id)
  }

  const stored = () =>
    typeof window === 'undefined' ? '' : (window.localStorage.getItem(SPACE_KEY) ?? '')

  const loadTree = async (id: string) => {
    spaceId.value = id
    remember(id)
    pages.value = await getTree(id)
  }

  const ensureSpaces = async () => {
    if (spaces.value.length) return

    isLoading.value = true
    error.value = ''

    try {
      spaces.value = await listSpaces()

      const wanted = spaces.value.find((entry) => entry.id === stored()) ?? spaces.value[0]

      if (wanted) await loadTree(wanted.id)
    } catch {
      error.value = 'load'
    } finally {
      isLoading.value = false
    }
  }

  const selectSpace = async (id: string) => {
    page.value = null
    links.value = []
    await loadTree(id)
  }

  const openPage = async (pageId: string) => {
    isLoading.value = true
    error.value = ''

    try {
      page.value = await getPage(pageId)
      // Связи грузим следом: страница читается и без них, а ждать их значит
      // держать текст пустым лишние полсекунды.
      links.value = await listIssueLinks(pageId)

      if (page.value.spaceId && page.value.spaceId !== spaceId.value) {
        await loadTree(page.value.spaceId)
      }
    } catch {
      error.value = 'page'
    } finally {
      isLoading.value = false
    }
  }

  /** Правка страницы; ею же пишет автосейв, версии она не создаёт. */
  const savePage = async (title: string, contentMd: string) => {
    if (!page.value) return

    page.value = await updatePage(page.value.id, title, contentMd)
    // Заголовок виден в дереве сайдбара — без обновления там останется старый.
    await loadTree(page.value.spaceId || spaceId.value)
  }

  const loadVersions = async (pageId: string) => {
    versions.value = await listVersions(pageId)
  }

  const commitVersion = async (comment: string) => {
    if (!page.value) return

    await saveVersion(page.value.id, comment)
    page.value = await getPage(page.value.id)
    await loadVersions(page.value.id)
  }

  const restore = async (number: number) => {
    if (!page.value) return

    page.value = await restoreVersion(page.value.id, number)
    await loadVersions(page.value.id)
    await loadTree(page.value.spaceId || spaceId.value)
  }

  /** Новая страница: пустая, с рабочим заголовком — его правят в редакторе. */
  const create = async (parentId: string, title: string) => {
    const created = await createPage(spaceId.value, parentId, title)

    await loadTree(spaceId.value)

    return created
  }

  const move = async (id: string, parentId: string) => {
    await movePage(id, parentId)
    await loadTree(spaceId.value)
  }

  const remove = async (id: string) => {
    await deletePage(id)

    if (page.value?.id === id) {
      page.value = null
      links.value = []
      versions.value = []
    }

    await loadTree(spaceId.value)
  }

  return {
    spaces,
    spaceId,
    space,
    pages,
    tree,
    page,
    links,
    versions,
    isLoading,
    error,
    ensureSpaces,
    selectSpace,
    openPage,
    savePage,
    loadVersions,
    commitVersion,
    restore,
    create,
    move,
    remove,
  }
})
